import React from 'react';
import Icon from '../../../components/AppIcon';

const DiversityStats = () => {
  const industryData = [
    { name: "Technology", percentage: 28, count: 42, color: "bg-blue-500" },
    { name: "Healthcare", percentage: 22, count: 33, color: "bg-green-500" },
    { name: "Finance", percentage: 18, count: 27, color: "bg-purple-500" },
    { name: "Education", percentage: 15, count: 23, color: "bg-orange-500" },
    { name: "Engineering", percentage: 12, count: 18, color: "bg-red-500" },
    { name: "Others", percentage: 5, count: 7, color: "bg-gray-500" }
  ];

  const educationData = [
    { name: "University of Colombo", count: 35, percentage: 23 },
    { name: "University of Moratuwa", count: 28, percentage: 19 },
    { name: "SLIIT", count: 22, percentage: 15 },
    { name: "University of Peradeniya", count: 18, percentage: 12 },
    { name: "University of Kelaniya", count: 15, percentage: 10 },
    { name: "Other Universities", count: 32, percentage: 21 }
  ];

  const serviceInterests = [
    { name: "Education & Youth Development", count: 89, icon: "GraduationCap" },
    { name: "Healthcare & Wellness", count: 76, icon: "Heart" },
    { name: "Environmental Conservation", count: 68, icon: "Leaf" },
    { name: "Technology & Innovation", count: 54, icon: "Laptop" },
    { name: "Community Development", count: 92, icon: "Users" },
    { name: "Arts & Culture", count: 43, icon: "Palette" }
  ];

  const leadershipRoles = [
    { role: "Committee Chairs", count: 24, description: "Leading specialized committees" },
    { role: "Project Leaders", count: 67, description: "Managing service projects" },
    { role: "Mentors", count: 45, description: "Guiding new members" },
    { role: "External Representatives", count: 18, description: "Representing club externally" }
  ];

  const demographicStats = [
    { label: "Average Age", value: "26", unit: "years", icon: "Calendar" },
    { label: "Gender Balance", value: "52:48", unit: "F:M ratio", icon: "Users" },
    { label: "Professional Experience", value: "4.2", unit: "years avg", icon: "Briefcase" },
    { label: "Retention Rate", value: "94%", unit: "annual", icon: "TrendingUp" }
  ];

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Diversity & Representation
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Our strength lies in our diversity. Explore the rich tapestry of backgrounds, 
            expertise, and perspectives that make Leo Circle Colombo a dynamic community.
          </p>
        </div>

        {/* Demographic Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {demographicStats.map((stat, index) => (
            <div key={index} className="bg-card rounded-xl p-6 text-center card-shadow">
              <div className="w-12 h-12 hero-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name={stat.icon} size={24} className="text-white" />
              </div>
              <div className="text-2xl lg:text-3xl font-bold text-foreground mb-1">{stat.value}</div>
              <div className="text-sm text-muted-foreground mb-1">{stat.unit}</div>
              <div className="text-xs text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Industry Distribution */}
          <div className="bg-card rounded-xl p-8 card-shadow">
            <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
              <Icon name="BarChart3" size={24} className="text-primary mr-3" />
              Industry Distribution
            </h3>
            <div className="space-y-4">
              {industryData.map((industry, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className={`w-4 h-4 rounded-full ${industry.color} mr-3`}></div>
                    <span className="text-foreground font-medium">{industry.name}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-32 bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${industry.color}`}
                        style={{ width: `${industry.percentage}%` }}
                      ></div>
                    </div>
                    <div className="text-sm text-muted-foreground w-12 text-right">
                      {industry.count}
                    </div>
                    <div className="text-sm font-medium text-foreground w-8 text-right">
                      {industry.percentage}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Educational Background */}
          <div className="bg-card rounded-xl p-8 card-shadow">
            <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
              <Icon name="GraduationCap" size={24} className="text-primary mr-3" />
              Educational Background
            </h3>
            <div className="space-y-4">
              {educationData.map((edu, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <span className="text-foreground font-medium">{edu.name}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-24 bg-muted rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-accent"
                        style={{ width: `${edu.percentage}%` }}
                      ></div>
                    </div>
                    <div className="text-sm text-muted-foreground w-8 text-right">
                      {edu.count}
                    </div>
                    <div className="text-sm font-medium text-foreground w-8 text-right">
                      {edu.percentage}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Service Interests */}
        <div className="bg-card rounded-xl p-8 card-shadow mb-16">
          <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
            <Icon name="Heart" size={24} className="text-primary mr-3" />
            Service Interest Areas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceInterests.map((interest, index) => (
              <div key={index} className="flex items-center p-4 bg-surface rounded-lg">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name={interest.icon} size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">{interest.name}</h4>
                  <p className="text-sm text-muted-foreground">{interest.count} members interested</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leadership Roles */}
        <div className="bg-card rounded-xl p-8 card-shadow">
          <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
            <Icon name="Crown" size={24} className="text-primary mr-3" />
            Leadership Participation
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {leadershipRoles.map((role, index) => (
              <div key={index} className="text-center p-6 bg-surface rounded-lg">
                <div className="text-3xl font-bold text-primary mb-2">{role.count}</div>
                <h4 className="font-medium text-foreground mb-2">{role.role}</h4>
                <p className="text-sm text-muted-foreground">{role.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Add Your Voice to Our Diverse Community
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Every background brings unique value. Join professionals from across industries 
              who are united by a common purpose: serving our community with excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-primary px-6 py-3 rounded-lg font-medium hover:bg-white/90 transition-smooth">
                Explore Membership
              </button>
              <button className="border border-white/30 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-smooth">
                View Member Profiles
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DiversityStats;